import { Image } from '@lumapps/lumx-images/types';
import { Theme } from '@lumapps/lumx/react';
import { TranslateObject } from '@lumapps/translations';
import { Block, LegacyWidgetProperties } from '@lumapps/widget-base/types';
import { ClientComputedProps } from '@lumapps/widget-base/types/client-computed';

/**
 * Link property keys - used to ensure type safety when picking/omitting properties
 */
export const LinkPropertyKeys = [
    'description',
    'title',
    'link',
    'targetBlank',
    'thumbnail',
    'blobKey',
    'image',
] as const;

/**
 * Interface used for creating/editing a link. It neeeds to be
 * different than the interface BlockLink since when creating
 * a link, we need to add translations for each field, so types
 * for description, title and link are different.
 */
export interface Link {
    description?: TranslateObject;
    title: TranslateObject;
    link: TranslateObject;
    targetBlank?: boolean;
    thumbnail?: string;
    blobKey?: string;
    image?: Image;
}

/**
 * EditableLink extends Link with a required unique identifier
 * This ID is used for list operations (reordering, deletion) in the editor
 */
export interface EditableLink extends Link {
    id: string;
}

export interface BlockLink extends Block, Link {
    type: 'BlockLink';
    theme?: Theme;
    hasThumbnail?: boolean;
    noFallback?: boolean;
    overrideLanguage?: string;
    isEditor?: boolean;
}

export interface LegacyLinksProperties {
    items?: Link[];
    helper?: LegacyWidgetProperties['helper'];
    noResults?: LegacyWidgetProperties['noResults'];
    noFallback?: LegacyWidgetProperties['noFallback'];
    style?: LegacyWidgetProperties['style'];
    hasThumbnail?: boolean;
}

export type WidgetLinksProperties = LegacyWidgetProperties & LegacyLinksProperties;

export const EditableLinksPropertyKeys = ['items'] as const;

type EditableLinksKey = (typeof EditableLinksPropertyKeys)[number];

export type EditableLinksProperties = Pick<WidgetLinksProperties, EditableLinksKey>;

export interface ClientComputedLinksProps extends ClientComputedProps {
    properties?: LegacyLinksProperties;
}
